import { t } from "../../i18n";
import { Fragment, useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  LoaderCircle,
  Plus,
  Power,
  RefreshCw,
  TerminalSquare,
} from "lucide-react";
import type {
  AgentWorkspaceDefinition,
  AgentWorkspaceInstance,
  AgentWorkspacePolicy,
  AgentWorkspaceTestResult,
  SshHostKeyCandidate,
} from "@lumen/shared";
import {
  createAgentWorkspaceDraft,
  type Draft,
} from "../../components/agent-workspaces/AgentWorkspaceDraft";
export type { Draft } from "../../components/agent-workspaces/AgentWorkspaceDraft";
import { api } from "../../api";
import { AgentWorkspaceEditor } from "../../components/agent-workspaces/AgentWorkspaceEditor";
import { WorkspaceEditorLaunchButton } from "../../components/workspace-editor/WorkspaceEditorLaunchButton";
import {
  ConnectedMachineWorkspaceEditor,
  type ConnectedMachine,
} from "../../components/agent-workspaces/ConnectedMachineWorkspaceEditor";
import { completePersonalizationRequestFromLocation } from "../../personalization";
import { Link as RouterLink } from "react-router-dom";
import {
  isContainerWorkspaceType,
  normalizeAgentWorkspaceMode,
} from "../../components/agent-workspaces/workspace-type";

function fromRow(row: AgentWorkspaceDefinition): Draft {
  return {
    name: row.name,
    slug: row.slug,
    description: row.description,
    type: row.type,
    workspaceMode: normalizeAgentWorkspaceMode(row.workspaceMode),
    scope: row.scope,
    image: row.image ?? "",
    setupInstructions: row.setupInstructions ?? "",
    sshHost: row.sshHost ?? "",
    sshPort: row.sshPort,
    sshUsername: row.sshUsername ?? "",
    sshHostKeyFingerprint: row.sshHostKeyFingerprint ?? "",
    sshHostKeyAlgorithm: row.sshHostKeyAlgorithm ?? "",
    authScope: row.authScope,
    globalAuthType: row.globalAuthType ?? "key",
    password: "",
    privateKey: "",
    passphrase: "",
    startingFolder: row.startingFolder ?? "",
    commandPolicy: row.commandPolicy,
    allowUserPolicyRelaxation: row.allowUserPolicyRelaxation,
    cpuLimit: row.cpuLimit ? String(row.cpuLimit) : "",
    memoryLimitMb: row.memoryLimitMb ? String(row.memoryLimitMb) : "",
    networkEnabled: row.networkEnabled,
    commandTimeoutSeconds: row.commandTimeoutSeconds,
    enabled: row.enabled,
  };
}

function formatBytes(bytes: number | null) {
  if (bytes === null) return "—";
  const units = ["B", "MiB", "GiB", "TiB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)}${units[unit]}`;
}

export function AgentWorkspacesPage({
  canManage,
  canManagePlatform = false,
  canManageOrganization = false,
  organizationId,
  siteOrganizationManagement = false,
}: {
  canManage: boolean;
  canManagePlatform?: boolean;
  canManageOrganization?: boolean;
  organizationId?: string;
  siteOrganizationManagement?: boolean;
}) {
  const [rows, setRows] = useState<AgentWorkspaceDefinition[]>([]);
  const [selected, setSelected] = useState<string>();
  const initialScope = organizationId
    ? "organization"
    : canManagePlatform
      ? "platform"
      : "personal";
  const [draft, setDraft] = useState<Draft>(() =>
    createAgentWorkspaceDraft(initialScope),
  );
  const [userFolder, setUserFolder] = useState("");
  const [userPolicy, setUserPolicy] = useState<AgentWorkspacePolicy>({});
  const [userSshUsername, setUserSshUsername] = useState("");
  const [userAuthType, setUserAuthType] = useState<"password" | "key">("key");
  const [userPassword, setUserPassword] = useState("");
  const [userKey, setUserKey] = useState("");
  const [userPassphrase, setUserPassphrase] = useState("");
  const [instances, setInstances] = useState<AgentWorkspaceInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fetchingHostKeys, setFetchingHostKeys] = useState(false);
  const [hostKeyCandidates, setHostKeyCandidates] = useState<
    SshHostKeyCandidate[]
  >([]);
  const [testResult, setTestResult] = useState<AgentWorkspaceTestResult | null>(
    null,
  );
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [expandedProxmox, setExpandedProxmox] = useState<
    Record<string, boolean>
  >({});
  const [syncingProxmox, setSyncingProxmox] = useState<string | null>(null);
  const [machines, setMachines] = useState<ConnectedMachine[]>([]);
  const [enrollment, setEnrollment] = useState<{
    token: string;
    serverUrl: string;
  } | null>(null);
  const [machineBusy, setMachineBusy] = useState<string | null>(null);
  const [selectedMachineId, setSelectedMachineId] = useState<string>();
  const contextSuffix = organizationId
    ? `?organizationId=${encodeURIComponent(organizationId)}`
    : "";
  const manageWorkspace = canManage || siteOrganizationManagement;
  const load = useCallback(async () => {
    setLoading(true);
    const [workspaces, runtime, connected] = await Promise.all([
      api<AgentWorkspaceDefinition[]>(`/agent-workspaces${contextSuffix}`),
      organizationId
        ? api<AgentWorkspaceInstance[]>(
            `/agent-workspace-instances${contextSuffix}`,
          )
        : api<AgentWorkspaceInstance[]>("/agent-workspace-instances"),
      manageWorkspace
        ? api<ConnectedMachine[]>("/connected-machines")
        : Promise.resolve([] as ConnectedMachine[]),
    ]);
    try {
      setRows(
        organizationId
          ? workspaces.filter((workspace) => workspace.scope === "organization")
          : workspaces,
      );
      setInstances(runtime);
      setMachines(connected);
    } finally {
      setLoading(false);
    }
  }, [contextSuffix, manageWorkspace, organizationId]);
  useEffect(() => {
    void load().catch((reason) => {
      setLoading(false);
      setError(
        reason instanceof Error ? reason.message : "Could not load workspaces",
      );
    });
  }, [load]);
  const choose = (row: AgentWorkspaceDefinition) => {
    setSelectedMachineId(undefined);
    setSelected(row.id);
    setDraft(fromRow(row));
    setUserFolder(row.userStartingFolder ?? "");
    setUserPolicy(row.userCommandPolicy);
    setUserSshUsername(row.userSshUsername ?? "");
    setUserAuthType(row.userAuthType ?? "key");
    setUserPassword("");
    setUserKey("");
    setUserPassphrase("");
    setHostKeyCandidates([]);
    setTestResult(null);
    setError("");
    setNotice("");
  };
  const createMachine = async (
    name: string,
    scope: ConnectedMachine["scope"],
  ) => {
    if (!name.trim()) {
      setError("Enter a name for this machine first.");
      return;
    }
    setMachineBusy("new");
    setError("");
    setNotice("");
    try {
      const result = await api<{
        enrollmentToken: string;
        serverUrl: string;
      }>("/connected-machines", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          scope,
        }),
      });
      setEnrollment({
        token: result.enrollmentToken,
        serverUrl: result.serverUrl,
      });
      setNotice(
        "Enrollment created. Copy the URL and one-time token to the machine.",
      );
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not create machine",
      );
    } finally {
      setMachineBusy(null);
    }
  };
  const rotateMachine = async (machine: ConnectedMachine) => {
    setMachineBusy(machine.id);
    setError("");
    setNotice("");
    try {
      const result = await api<{ enrollmentToken: string; serverUrl: string }>(
        `/connected-machines/${machine.id}/rotate`,
        { method: "POST" },
      );
      setEnrollment({
        token: result.enrollmentToken,
        serverUrl: result.serverUrl,
      });
      setNotice(`A new enrollment token was created for ${machine.name}.`);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not rotate machine credentials",
      );
    } finally {
      setMachineBusy(null);
    }
  };
  const revokeMachine = async (machine: ConnectedMachine) => {
    if (
      !window.confirm(
        `Revoke ${machine.name}? The machine will be disconnected.`,
      )
    )
      return;
    setMachineBusy(machine.id);
    setError("");
    setNotice("");
    try {
      await api(`/connected-machines/${machine.id}`, { method: "DELETE" });
      await load();
      setSelected(undefined);
      setSelectedMachineId(undefined);
      setEnrollment(null);
      setNotice(`${machine.name} was revoked and disconnected.`);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not revoke machine",
      );
    } finally {
      setMachineBusy(null);
    }
  };
  const copyEnrollment = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(`${label} copied to the clipboard.`);
    } catch {
      setError(
        `Could not copy the ${label.toLowerCase()}. Select it manually.`,
      );
    }
  };
  const fresh = () => {
    setSelectedMachineId(undefined);
    setSelected(undefined);
    setDraft(createAgentWorkspaceDraft(initialScope));
    setUserFolder("");
    setUserPolicy({});
    setUserSshUsername("");
    setHostKeyCandidates([]);
    setTestResult(null);
    setError("");
    setNotice("");
  };
  const credential = (
    authType: "password" | "key",
    password: string,
    privateKey: string,
    passphrase: string,
  ) =>
    authType === "password"
      ? password
        ? { password }
        : undefined
      : privateKey
        ? { privateKey, ...(passphrase ? { passphrase } : {}) }
        : undefined;
  const saveWorkspace = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      if (draft.type === "machine" && !selected) {
        await createMachine(draft.name, draft.scope);
        return;
      }
      const body = {
        name: draft.name,
        slug: draft.slug || undefined,
        description: draft.description,
        type: draft.type,
        ...(isContainerWorkspaceType(draft.type) && !selected
          ? { workspaceMode: draft.workspaceMode }
          : {}),
        scope: draft.scope,
        image: isContainerWorkspaceType(draft.type) ? draft.image : null,
        ...(isContainerWorkspaceType(draft.type)
          ? { setupInstructions: draft.setupInstructions || null }
          : {}),
        sshHost:
          draft.type === "ssh" || draft.type === "proxmox"
            ? draft.sshHost
            : null,
        sshPort: draft.sshPort,
        sshUsername:
          (draft.type === "ssh" || draft.type === "proxmox") &&
          draft.authScope === "global"
            ? draft.sshUsername
            : null,
        sshHostKeyFingerprint:
          draft.type === "ssh" || draft.type === "proxmox"
            ? draft.sshHostKeyFingerprint
            : null,
        sshHostKeyAlgorithm:
          draft.type === "ssh" || draft.type === "proxmox"
            ? draft.sshHostKeyAlgorithm
            : null,
        authScope: draft.authScope,
        globalAuthType:
          (draft.type === "ssh" || draft.type === "proxmox") &&
          draft.authScope === "global"
            ? draft.globalAuthType
            : null,
        credential:
          (draft.type === "ssh" || draft.type === "proxmox") &&
          draft.authScope === "global"
            ? credential(
                draft.globalAuthType,
                draft.password,
                draft.privateKey,
                draft.passphrase,
              )
            : undefined,
        startingFolder: draft.startingFolder || null,
        commandPolicy: draft.commandPolicy,
        allowUserPolicyRelaxation: draft.allowUserPolicyRelaxation,
        cpuLimit: draft.cpuLimit ? Number(draft.cpuLimit) : null,
        memoryLimitMb: draft.memoryLimitMb ? Number(draft.memoryLimitMb) : null,
        networkEnabled: draft.networkEnabled,
        commandTimeoutSeconds: draft.commandTimeoutSeconds,
        enabled: draft.enabled,
      };
      const saved = await api<AgentWorkspaceDefinition>(
        `${selected ? `/agent-workspaces/${selected}` : "/agent-workspaces"}${contextSuffix}`,
        { method: selected ? "PATCH" : "POST", body: JSON.stringify(body) },
      );
      await load();
      choose(saved);
      setNotice(selected ? "Workspace saved." : "Workspace created.");
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save workspace",
      );
    } finally {
      setSaving(false);
    }
  };
  const saveMine = async () => {
    if (!selected) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const row = rows.find((item) => item.id === selected)!;
      const saved = await api<AgentWorkspaceDefinition>(
        `/agent-workspaces/${selected}/user-settings`,
        {
          method: "PUT",
          body: JSON.stringify({
            startingFolder: userFolder || null,
            commandPolicy: userPolicy,
            ...(row.type === "ssh" && row.authScope === "user"
              ? {
                  sshUsername: userSshUsername,
                  authType: userAuthType,
                  credential: credential(
                    userAuthType,
                    userPassword,
                    userKey,
                    userPassphrase,
                  ),
                }
              : {}),
          }),
        },
      );
      await load();
      choose(saved);
      setNotice("Your workspace settings were saved.");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not save your settings",
      );
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!selected) return;
    const isProxmoxCluster =
      selectedRow?.type === "proxmox" &&
      selectedRow.proxmox?.targetKind === "host";
    if (
      !window.confirm(
        isProxmoxCluster
          ? t("Delete Proxmox cluster workspace “{{name}}”? {{reason}}", {
              name: selectedRow?.name ?? "",
              reason: t("This also removes its synced guest workspaces."),
            })
          : t("Delete workspace “{{name}}”? {{reason}}", {
              name: selectedRow?.name ?? "",
              reason: t("This cannot be undone."),
            }),
      )
    )
      return;
    setSaving(true);
    setNotice("");
    try {
      await api(`/agent-workspaces/${selected}${contextSuffix}`, {
        method: "DELETE",
      });
      await completePersonalizationRequestFromLocation({
        resourceType: isProxmoxCluster ? "proxmox_integration" : "workspace",
        resourceId: selectedRow?.proxmox?.integrationId ?? selected,
      }).catch(() => undefined);
      fresh();
      await load();
      setNotice("Workspace deleted.");
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not delete workspace",
      );
    } finally {
      setSaving(false);
    }
  };
  const test = async () => {
    if (!selected) return;
    setTesting(true);
    setTestResult(null);
    setError("");
    setNotice("");
    try {
      const result = await api<AgentWorkspaceTestResult>(
        `/agent-workspaces/${selected}/test${contextSuffix}`,
        { method: "POST", body: "{}" },
      );
      setTestResult(result);
      if (result.ok) {
        setNotice("Workspace test succeeded.");
        const row = rows.find((item) => item.id === selected);
        if (row)
          await completePersonalizationRequestFromLocation({
            resourceType:
              row.type === "proxmox" ? "proxmox_integration" : "workspace",
            resourceId: row.proxmox?.integrationId ?? row.id,
          }).catch(() => undefined);
      }
      await load();
    } catch (reason) {
      setTestResult({
        ok: false,
        message: reason instanceof Error ? reason.message : "Test failed",
      });
    } finally {
      setTesting(false);
    }
  };
  const fetchHostKeys = async () => {
    if (!draft.sshHost) return;
    setFetchingHostKeys(true);
    setHostKeyCandidates([]);
    setError("");
    try {
      setHostKeyCandidates(
        await api<SshHostKeyCandidate[]>(
          `/agent-workspaces/discover-ssh-host-keys${contextSuffix}`,
          {
            method: "POST",
            body: JSON.stringify({ host: draft.sshHost, port: draft.sshPort }),
          },
        ),
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not fetch SSH host keys",
      );
    } finally {
      setFetchingHostKeys(false);
    }
  };
  const stop = async (id: string) => {
    await api(`/agent-workspace-instances/${id}/stop${contextSuffix}`, {
      method: "POST",
      body: "{}",
    });
    await load();
  };
  const syncProxmox = async (integrationId: string) => {
    setSyncingProxmox(integrationId);
    setError("");
    setNotice("");
    try {
      await api(`/proxmox-integrations/${integrationId}/sync`, {
        method: "POST",
        body: "{}",
      });
      await load();
      setNotice("Proxmox workspaces synchronized.");
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not sync Proxmox",
      );
    } finally {
      setSyncingProxmox(null);
    }
  };
  const selectedRow = rows.find((row) => row.id === selected);
  const selectedMachineWorkspace =
    selectedRow?.type === "machine" ? selectedRow.machine : null;
  const machineWorkspaceGroups = new Map<string, AgentWorkspaceDefinition[]>();
  for (const row of rows) {
    if (row.type !== "machine" || !row.machine) continue;
    const group = machineWorkspaceGroups.get(row.machine.id) ?? [];
    group.push(row);
    machineWorkspaceGroups.set(row.machine.id, group);
  }
  const selectedMachine = selectedMachineWorkspace
    ? machines.find((machine) => machine.id === selectedMachineWorkspace.id)
    : selectedMachineId
      ? machines.find((machine) => machine.id === selectedMachineId)
      : undefined;
  const representedMachineIds = new Set(
    rows.flatMap((row) => (row.machine ? [row.machine.id] : [])),
  );
  const unrepresentedMachines = machines.filter(
    (machine) => !representedMachineIds.has(machine.id),
  );
  const editable = !selected || Boolean(selectedRow?.editable);
  const childrenByParent = new Map<string, AgentWorkspaceDefinition[]>();
  for (const row of rows) {
    if (!row.parentWorkspaceId) continue;
    const children = childrenByParent.get(row.parentWorkspaceId) ?? [];
    children.push(row);
    childrenByParent.set(row.parentWorkspaceId, children);
  }
  const roots = rows.filter((row) => !row.parentWorkspaceId);
  const workspaceRoots = roots.filter((row) => row.type !== "machine");
  for (const group of machineWorkspaceGroups.values()) {
    const representative = group[0];
    if (representative) workspaceRoots.push(representative);
  }
  const workspaceButton = (row: AgentWorkspaceDefinition, nested = false) => (
    <button
      key={row.id}
      onClick={() => choose(row)}
      className={`w-full rounded-xl p-3 text-left ${nested ? "pl-8" : ""} ${selected === row.id ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <strong className="truncate text-sm">
          {row.type === "machine" ? (row.machine?.name ?? row.name) : row.name}
        </strong>
        <span className="flex shrink-0 items-center gap-1.5 text-[10px] text-stone-400 uppercase">
          {row.workspaceMode === "machine" && (
            <span className="rounded bg-lumen-100 px-1.5 py-0.5 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
              {t("Machine · shared")}
            </span>
          )}
          <span>
            {row.proxmox?.targetKind === "lxc"
              ? "LXC"
              : row.proxmox?.targetKind === "qemu"
                ? "VM"
                : row.scope}
          </span>
        </span>
      </div>
      <p className="mt-1 truncate text-xs text-stone-500">
        {isContainerWorkspaceType(row.type)
          ? row.image
          : row.type === "machine"
            ? `${row.machine?.name ?? "Connected machine"} · ${machineWorkspaceGroups.get(row.machine?.id ?? "")?.length ?? 0} shared folder${(machineWorkspaceGroups.get(row.machine?.id ?? "")?.length ?? 0) === 1 ? "" : "s"}`
            : row.type === "proxmox"
              ? row.proxmox?.targetKind === "host"
                ? row.description
                : `${row.proxmox?.node} · ${row.proxmox?.status ?? "unknown"}${row.proxmox?.executionStatus === "unavailable" ? " · unavailable" : ""}`
              : `${row.authScope === "global" ? `${row.sshUsername}@` : ""}${row.sshHost}:${row.sshPort}`}
      </p>
      {!nested && row.type === "proxmox" && row.proxmox && (
        <p className="mt-1 truncate text-[11px] text-stone-400">
          {t("guest", { count: childrenByParent.get(row.id)?.length ?? 0 })}
          {row.proxmox.lastSyncError
            ? ` · ${row.proxmox.lastSyncError}`
            : row.proxmox.lastSyncAt
              ? ` · ${t("Synced {{date}}", {
                  date: new Date(row.proxmox.lastSyncAt).toLocaleString(),
                })}`
              : ""}
        </p>
      )}
    </button>
  );
  return (
    <section className="min-w-0 flex-1 overflow-y-auto p-5 md:p-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
            <TerminalSquare size={20} />
          </span>
          <div>
            <h1 className="text-2xl font-bold">{t("Agent workspaces")}</h1>
            <p className="text-sm text-stone-500">
              {t(
                "Persistent development environments exposed to models through one guarded workspace tool.",
              )}
            </p>
          </div>
        </div>
        {(error || notice) && (
          <div
            role={error ? "alert" : "status"}
            className={`mb-5 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${error ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300" : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:text-emerald-300"}`}
          >
            {error ? <AlertCircle size={17} /> : <CheckCircle2 size={17} />}
            <span>{error || notice}</span>
          </div>
        )}
        <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
          <div className="panel p-2">
            {manageWorkspace && (
              <button
                className="button mb-2 w-full"
                onClick={fresh}
                disabled={loading}
              >
                <Plus size={16} /> {t("New workspace")}
              </button>
            )}
            {loading && (
              <div className="flex justify-center p-5 text-stone-400">
                <LoaderCircle className="animate-spin" size={18} />
              </div>
            )}
            {workspaceRoots.map((row) => {
              const children = childrenByParent.get(row.id) ?? [];
              const expanded = expandedProxmox[row.id] ?? false;
              return (
                <Fragment key={row.id}>
                  <div className="flex items-stretch">
                    {row.type === "proxmox" && children.length > 0 && (
                      <button
                        className="px-1 text-stone-400"
                        onClick={() =>
                          setExpandedProxmox((current) => ({
                            ...current,
                            [row.id]: !expanded,
                          }))
                        }
                        aria-label={t("Toggle Proxmox guests")}
                      >
                        {expanded ? (
                          <ChevronDown size={15} />
                        ) : (
                          <ChevronRight size={15} />
                        )}
                      </button>
                    )}
                    <div className="min-w-0 flex-1">{workspaceButton(row)}</div>
                    {!siteOrganizationManagement &&
                      row.enabled &&
                      row.type !== "proxmox" && (
                        <WorkspaceEditorLaunchButton
                          workspaceId={row.id}
                          workspaceName={row.name}
                          onError={setError}
                        />
                      )}
                    {row.type === "proxmox" && row.proxmox && (
                      <button
                        className="px-2 text-stone-400 hover:text-lumen-600"
                        disabled={syncingProxmox === row.proxmox.integrationId}
                        onClick={(event) => {
                          event.stopPropagation();
                          void syncProxmox(row.proxmox!.integrationId);
                        }}
                        title={t("Sync now")}
                      >
                        <RefreshCw
                          size={15}
                          className={
                            syncingProxmox === row.proxmox.integrationId
                              ? "animate-spin"
                              : ""
                          }
                        />
                      </button>
                    )}
                  </div>
                  {row.type === "proxmox" &&
                    expanded &&
                    children.map((child) => workspaceButton(child, true))}
                </Fragment>
              );
            })}
            {manageWorkspace && unrepresentedMachines.length > 0 && (
              <div className="mt-2 border-t border-stone-100 pt-2 dark:border-white/10">
                {unrepresentedMachines.map((machine) => (
                  <button
                    key={machine.id}
                    type="button"
                    onClick={() => {
                      setSelected(undefined);
                      setSelectedMachineId(machine.id);
                      setEnrollment(null);
                      setError("");
                      setNotice("");
                    }}
                    className={`w-full rounded-xl p-3 text-left ${selectedMachineId === machine.id ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <strong className="truncate text-sm">
                        {machine.name}
                      </strong>
                      <span className="text-[10px] text-stone-400 uppercase">
                        {t("Machine")}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-stone-500">
                      {machine.platform} · {machine.architecture} ·{" "}
                      {t("{{status}} · No shared folders", {
                        status: machine.status,
                      })}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedRow?.type === "machine" && selectedMachineWorkspace ? (
            <ConnectedMachineWorkspaceEditor
              workspaces={(
                machineWorkspaceGroups.get(selectedMachineWorkspace.id) ?? []
              ).flatMap((row) => (row.machine ? [row.machine] : []))}
              machine={selectedMachine}
              enrollment={enrollment}
              machineBusy={machineBusy}
              onRotate={() => {
                if (selectedMachine) void rotateMachine(selectedMachine);
              }}
              onRevoke={() => {
                if (selectedMachine) void revokeMachine(selectedMachine);
              }}
              onCopy={(value, label) => void copyEnrollment(value, label)}
            />
          ) : selectedMachineId && selectedMachine ? (
            <ConnectedMachineWorkspaceEditor
              machine={selectedMachine}
              enrollment={enrollment}
              machineBusy={machineBusy}
              onRotate={() => void rotateMachine(selectedMachine)}
              onRevoke={() => void revokeMachine(selectedMachine)}
              onCopy={(value, label) => void copyEnrollment(value, label)}
            />
          ) : (
            <AgentWorkspaceEditor
              selected={selected}
              manageWorkspace={manageWorkspace}
              editable={editable}
              draft={draft}
              setDraft={setDraft}
              selectedRow={selectedRow}
              canManagePlatform={canManagePlatform}
              canManageOrganization={canManageOrganization}
              testing={testing}
              test={test}
              saving={saving}
              remove={remove}
              saveWorkspace={saveWorkspace}
              testResult={testResult}
              error={error}
              siteOrganizationManagement={siteOrganizationManagement}
              userFolder={userFolder}
              setUserFolder={setUserFolder}
              userPolicy={userPolicy}
              setUserPolicy={setUserPolicy}
              userSshUsername={userSshUsername}
              setUserSshUsername={setUserSshUsername}
              userAuthType={userAuthType}
              setUserAuthType={setUserAuthType}
              userPassword={userPassword}
              setUserPassword={setUserPassword}
              userKey={userKey}
              setUserKey={setUserKey}
              userPassphrase={userPassphrase}
              setUserPassphrase={setUserPassphrase}
              saveMine={saveMine}
              fetchingHostKeys={fetchingHostKeys}
              fetchHostKeys={fetchHostKeys}
              hostKeyCandidates={hostKeyCandidates}
              setHostKeyCandidates={setHostKeyCandidates}
              machineEnrollment={enrollment}
              copyMachineEnrollment={(value, label) =>
                void copyEnrollment(value, label)
              }
            />
          )}
        </div>
        <div className="panel mt-5 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-bold">{t("Container instances")}</h2>
            <RouterLink
              className="button text-xs"
              to="/settings/workspace-volumes"
            >
              <ArrowUpRight size={14} />
              {t("View workspace volumes")}
            </RouterLink>
          </div>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              "Your persistent containers are listed here. Workspace managers can also stop other users’ containers.",
            )}
          </p>
          <div className="mt-4 divide-y divide-stone-100 dark:divide-white/10">
            {instances.length ? (
              instances.map((instance) => (
                <div
                  key={instance.id}
                  className="flex flex-wrap items-center gap-3 py-3 text-sm"
                >
                  <TerminalSquare size={16} className="text-lumen-600" />
                  <span className="font-semibold">
                    {instance.workspaceName}
                  </span>
                  <span className="text-stone-400">{instance.userName}</span>
                  <span className="text-stone-500">
                    {t("Conversation")}:{" "}
                    {instance.conversationTitle ??
                      instance.conversationId ??
                      t("None")}
                  </span>
                  <span className="text-stone-500">
                    {t("Repository")}:{" "}
                    {instance.repositoryNames?.length
                      ? instance.repositoryNames.join(", ")
                      : t("None")}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${instance.status === "running" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}
                  >
                    {instance.orphaned ? "orphaned" : instance.status}
                  </span>
                  {instance.resources && (
                    <div className="basis-full pl-7 text-xs text-stone-500">
                      {instance.resources.cpuPercent !== null && (
                        <span className="mr-3">
                          CPU {instance.resources.cpuPercent.toFixed(1)}%
                          {instance.resources.cpuLimit !== null
                            ? ` / ${instance.resources.cpuLimit} core${instance.resources.cpuLimit === 1 ? "" : "s"}`
                            : ""}
                        </span>
                      )}
                      {instance.resources.memoryBytes !== null && (
                        <span className="mr-3">
                          Memory {formatBytes(instance.resources.memoryBytes)}
                          {instance.resources.memoryLimitBytes !== null
                            ? ` / ${formatBytes(instance.resources.memoryLimitBytes)}`
                            : ""}
                        </span>
                      )}
                      {instance.resources.pids !== null && (
                        <span className="mr-3">
                          PIDs {instance.resources.pids}
                          {instance.resources.pidsLimit !== null
                            ? ` / ${instance.resources.pidsLimit}`
                            : ""}
                        </span>
                      )}
                      {instance.resources.oomKilled === true && (
                        <span className="font-semibold text-red-600">
                          OOM killed
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    className="button ml-auto text-xs"
                    disabled={
                      !instance.orphaned && instance.status !== "running"
                    }
                    onClick={() => void stop(encodeURIComponent(instance.id))}
                  >
                    <Power size={14} />
                    {instance.orphaned ? t("Clean up") : t("Shut down")}
                  </button>
                </div>
              ))
            ) : (
              <p className="py-5 text-sm text-stone-400">
                {t("No workspace containers have been created yet.")}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
